import { When } from 'react-if'
import { Durations } from '@/constants/durations'
import { paths } from '@/constants/paths'
import { slugs } from '@/constants/slugs'
import { ProjectMetaProvider } from '@/contexts/ProjectMetaContext'
import { TheatricalRegionProvider } from '@/contexts/TheatricalRegionContext/TheatricalRegionContext'
import { Seo } from '@/layout/Seo'
import { TVSeriesJsonLd } from '@/layout/Seo/JsonLd'
import { NextPageWithLayout } from '@/layouts/types'
import { MarketingVideoPopup } from '@/organisms/MarketingVideoPopup'
import { TheatricalFooter, TheatricalFooterProps } from '@/organisms/TheatricalFooter'
import { getTheatricalReleaseBySlug } from '@/services/CmsService/TheatricReleases'
import { CatalogSeries, fetchTitle, mapJsonLdProps, mapSeoProps } from '@/services/ContentCatalog'
import { getLightFranchiseBySlug, LightFranchise } from '@/services/FranchiseService'
import { getProjectLivestreams, PlayableLivestream } from '@/services/LivestreamService'
import { ContentWithWatchableReasons, isStreaming } from '@/services/ProjectPhaseService'
import { getContentfulProjectTheme } from '@/services/ProjectThemeService'
import { getProjectAbout, getWatchableContentByGuid, ProjectMeta } from '@/services/ProjectsService'
import {
  formatFilmName,
  getTheatricalPreReleasePageData,
  GetTheatricalPreReleasePageDataResult,
  isProjectThatsBothSeriesAndMovie,
  isTheChosen,
  useSeriesTitle,
} from '@/services/TheatricalPreReleaseService'
import { getWhereToWatch, WhereToWatch } from '@/services/WhereToWatchService'
import { ProjectTheme, TheatricalRegion } from '@/types/codegen-contentful'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { isDevelopmentEnvironment } from '@/utils/environment-utils'
import { getStaticPropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { noPreRenderedStaticPaths } from '@/utils/nextUtils/staticPages'
import { omitUndefineds } from '@/utils/object'
import { isSeries, isSpecial } from '@/utils/project-utils'
import { shouldHideProjectFromSearchEngines } from '@/utils/seo/searchEngineHelper'
import { loadTranslations } from '@/utils/translate/translate-server'
import { WatchTheatricalView } from '@/views/WatchTheatricalView'
import { WatchTheatricalContextProvider } from '@/views/WatchTheatricalView/WatchTheatricalContext'

interface ShowsPageProps extends ProjectMeta {
  catalogTitle: CatalogSeries | null
  isStreaming: boolean
  livestreams: PlayableLivestream[] | null
  locale: string
  pageData: GetTheatricalPreReleasePageDataResult
  supportedTheatricalRegions: TheatricalRegion[] | null
  theatricalRegion: TheatricalRegion[] | null
  watchableContent: ContentWithWatchableReasons | null
  whereToWatch: WhereToWatch | null
  contentfulProjectTheme: ProjectTheme
  franchise: LightFranchise | null
  projectMeta: ProjectMeta
}

export const ShowsPage: NextPageWithLayout<ShowsPageProps> = (props) => {
  const { filmDetails } = props.pageData
  const { isStreaming, locale, franchise, projectMeta } = props
  const shouldHide = shouldHideProjectFromSearchEngines(props)

  const path = `${paths.shows.index}/${props.slug}`
  const catalogTitle = props.catalogTitle
  const title = useSeriesTitle({
    filmName: catalogTitle ? formatFilmName(catalogTitle) : projectMeta.name,
    isStreaming,
    releaseYear: filmDetails?.releaseYear,
  })

  return (
    <>
      <Seo
        {...mapSeoProps({ path, title, catalogTitle, locale, projectMeta })}
        noindex={shouldHide}
        nofollow={shouldHide}
        path={path}
      />
      {catalogTitle && <TVSeriesJsonLd {...mapJsonLdProps({ path, catalogTitle, locale })} />}
      <ProjectMetaProvider project={props}>
        <TheatricalRegionProvider
          supportedTheatricalRegions={props.supportedTheatricalRegions || []}
          regionData={props.theatricalRegion || []}
        >
          <WatchTheatricalContextProvider pageData={props.pageData} franchise={franchise}>
            <MarketingVideoPopup />
            <WatchTheatricalView
              catalogTitle={catalogTitle}
              livestreams={props.livestreams}
              whereToWatch={props.whereToWatch}
              contentfulProjectTheme={props.contentfulProjectTheme}
              watchableContent={props.watchableContent}
              isStreaming={props.isStreaming}
            />
            <When condition={props.slug === slugs.gabrielAndTheGuardians || props.slug === slugs.tuttleTwins}>
              <TheatricalFooter {...(filmDetails?.filmAssociationLogos as TheatricalFooterProps)} slug={props.slug} />
            </When>
          </WatchTheatricalContextProvider>
        </TheatricalRegionProvider>
      </ProjectMetaProvider>
    </>
  )
}

export const getStaticPaths = noPreRenderedStaticPaths

export const getStaticProps = getStaticPropsErrorHandler<ShowsPageProps>(
  { filePath: 'pages/[locale]/shows/[projectSlug]/index.tsx' },
  async ({ federationClient, locale, next404, nextRedirect, params, preview }) => {
    const project = getItemOrFirstEntry(params.projectSlug)?.toLowerCase()
    if (!project) return nextRedirect(paths.shows.index, Durations.FIFTEEN_MINUTES_IN_SECONDS)

    let projectMeta = await getProjectAbout({ slug: project }, federationClient, { locale, preview })
    if (!projectMeta) return nextRedirect(paths.shows.index, Durations.FIFTEEN_MINUTES_IN_SECONDS)

    const shouldIncludePreview = isDevelopmentEnvironment()

    const [
      livestreams,
      pageData,
      whereToWatch,
      contentfulProjectTheme,
      theatricalRelease,
      watchableContent,
      translations,
    ] = await Promise.all([
      getProjectLivestreams(projectMeta, project, federationClient),
      getTheatricalPreReleasePageData({ slug: project }, federationClient, { locale, preview }),
      getWhereToWatch(project, preview),
      getContentfulProjectTheme(project, preview),
      getTheatricalReleaseBySlug({
        theatricalSlug: project,
        opts: { preview: shouldIncludePreview, locale },
      }),
      getWatchableContentByGuid(projectMeta?.title?.id, federationClient),
      loadTranslations(locale, [
        'common',
        'home',
        'watch',
        'app-promo',
        'ads',
        'account',
        'theatrical-presales',
        'pif',
        'guild',
      ]),
    ])

    const catalogTitle = pageData.contentCatalogId
      ? await fetchTitle<CatalogSeries>(locale, projectMeta?.title?.id ?? pageData.contentCatalogId)
      : null

    let franchise: LightFranchise | null = null
    if (projectMeta.franchiseDetails?.slug) {
      franchise = await getLightFranchiseBySlug(projectMeta.franchiseDetails.slug)
    }

    const regionData = theatricalRelease?.regionCollection || null
    const supportedTheatricalRegions = theatricalRelease?.supportedTheatricalRegions || null

    // Projects may have a hub page and not be in Hydra so these values are undefined
    if (projectMeta.slug === undefined) {
      projectMeta = { ...projectMeta, slug: project }
    }

    if (projectMeta.public === undefined || (!projectMeta.public && pageData?.filmDetails?.sys?.id && catalogTitle)) {
      projectMeta = { ...projectMeta, public: true }
    }

    const redirect = getRedirect(project)
    if (redirect) return redirect

    if (!contentfulProjectTheme) return next404(Durations.FIFTEEN_MINUTES_IN_SECONDS)

    if (
      (!isSeries(projectMeta) && !isSpecial(projectMeta) && !isProjectThatsBothSeriesAndMovie(projectMeta)) ||
      isTheChosen(project)
    ) {
      return {
        redirect: {
          destination: paths.shows.index,
          permanent: false,
        },
      }
    }

    return {
      props: {
        catalogTitle: catalogTitle ? omitUndefineds(catalogTitle) : null,
        isStreaming: isStreaming(watchableContent),
        livestreams,
        locale,
        pageData,
        supportedTheatricalRegions,
        theatricalRegion: regionData,
        watchableContent,
        whereToWatch,
        franchise: franchise ? omitUndefineds(franchise) : null,
        contentfulProjectTheme,
        projectMeta,
        ...projectMeta,
        ...translations,
      },
      revalidate: catalogTitle ? Durations.FIFTEEN_MINUTES_IN_SECONDS : Durations.ONE_MINUTE_IN_SECONDS,
    }
  },
)

export default ShowsPage

const getRedirect = (project: string) => {
  if (project === slugs.truthAndConviction) {
    return {
      redirect: {
        destination: paths.shows.truthAndTreasonSeries,
        permanent: false,
      },
    }
  }

  return null
}
